import * as THREE from 'three';
import { frameAtDistance, type SampledTrack } from './track.ts';

export interface SimulationConfig {
  gravity: number;
  rollingResistance: number;
  aerodynamicDrag: number;
  fixedStep: number;
  liftTargetSpeed: number;
  brakeTargetSpeed: number;
}

export interface SimulationState {
  distance: number;
  speed: number;
  elapsed: number;
  verticalG: number;
  lateralG: number;
  longitudinalG: number;
  peakSpeed: number;
  peakAbsG: number;
}

export const DEFAULT_CONFIG: SimulationConfig = {
  gravity: 9.80665,
  rollingResistance: 0.085,
  aerodynamicDrag: 0.0018,
  fixedStep: 1 / 120,
  liftTargetSpeed: 4.5,
  brakeTargetSpeed: 6,
};

export function initialState(distance = 0, speed = 3): SimulationState {
  return { distance, speed, elapsed: 0, verticalG: 1, lateralG: 0, longitudinalG: 0, peakSpeed: speed, peakAbsG: 1 };
}

function semanticAcceleration(
  track: SampledTrack,
  distance: number,
  speed: number,
  gravityAlongTrack: number,
  resistance: number,
  config: SimulationConfig,
): number {
  const phase = ((distance % track.length) + track.length) % track.length / track.length;
  // Demonstrates data-driven semantic sections: station/lift through the first crest, brakes last 9%.
  if (phase < 0.23) {
    return -gravityAlongTrack + resistance + THREE.MathUtils.clamp((config.liftTargetSpeed - speed) * 2.5, -5, 5);
  }
  if (phase > 0.91) {
    return -gravityAlongTrack + resistance + THREE.MathUtils.clamp((config.brakeTargetSpeed - speed) * 1.8, -7, 2);
  }
  return 0;
}

export function simulateStep(track: SampledTrack, state: SimulationState, config = DEFAULT_CONFIG): SimulationState {
  const before = frameAtDistance(track, state.distance);
  const gravityAlongTrack = -config.gravity * before.tangent.y;
  const resistance = state.speed > 0
    ? config.rollingResistance * config.gravity + config.aerodynamicDrag * state.speed * state.speed
    : 0;
  const powered = semanticAcceleration(track, state.distance, state.speed, gravityAlongTrack, resistance, config);
  const longitudinalAcceleration = gravityAlongTrack + powered - resistance;
  const speed = Math.max(0, state.speed + longitudinalAcceleration * config.fixedStep);
  const distance = (state.distance + speed * config.fixedStep) % track.length;
  const after = frameAtDistance(track, distance);
  const worldAcceleration = after.tangent.clone().multiplyScalar(longitudinalAcceleration)
    .add(after.tangent.clone().sub(before.tangent).multiplyScalar(speed / config.fixedStep));
  const apparent = worldAcceleration.clone().sub(new THREE.Vector3(0, -config.gravity, 0));
  const verticalG = apparent.dot(after.normal) / config.gravity;
  const lateralG = apparent.dot(after.binormal) / config.gravity;
  const longitudinalG = apparent.dot(after.tangent) / config.gravity;
  const peakAbsG = Math.max(state.peakAbsG, Math.abs(verticalG), Math.abs(lateralG), Math.abs(longitudinalG));
  return {
    distance,
    speed,
    elapsed: state.elapsed + config.fixedStep,
    verticalG,
    lateralG,
    longitudinalG,
    peakSpeed: Math.max(state.peakSpeed, speed),
    peakAbsG,
  };
}

export interface FixtureResult {
  name: string;
  passed: boolean;
  expected: string;
  actual: string;
}

export function runDeterministicFixtures(track: SampledTrack): FixtureResult[] {
  const firstRun = initialState(0, 3);
  const secondRun = initialState(0, 3);
  let a = firstRun;
  let b = secondRun;
  for (let i = 0; i < 2400; i += 1) {
    a = simulateStep(track, a);
    b = simulateStep(track, b);
  }
  const deterministic = Math.abs(a.distance - b.distance) < 1e-10 && Math.abs(a.speed - b.speed) < 1e-10;
  const finite = [a.distance, a.speed, a.verticalG, a.lateralG, a.longitudinalG, a.peakSpeed, a.peakAbsG]
    .every(Number.isFinite);
  const nonNegativeSpeed = a.speed >= 0;
  const poweredMotion = a.speed > 0.1 && a.peakSpeed > 4;

  const flatPoints = [
    new THREE.Vector3(-10, 1, 0), new THREE.Vector3(-4, 1, 0),
    new THREE.Vector3(4, 1, 0), new THREE.Vector3(10, 1, 0),
  ];
  // An explicit analytical drag check is renderer- and curve-independent.
  const dt = DEFAULT_CONFIG.fixedStep;
  let flatSpeed = 12;
  for (let i = 0; i < 120; i += 1) {
    const loss = DEFAULT_CONFIG.rollingResistance * DEFAULT_CONFIG.gravity
      + DEFAULT_CONFIG.aerodynamicDrag * flatSpeed * flatSpeed;
    flatSpeed = Math.max(0, flatSpeed - loss * dt);
  }
  return [
    { name: 'fixed-step determinism', passed: deterministic, expected: 'two 20 s runs match within 1e-10', actual: `Δd=${Math.abs(a.distance - b.distance).toExponential(2)}, Δv=${Math.abs(a.speed - b.speed).toExponential(2)}` },
    { name: 'finite state', passed: finite, expected: 'no NaN or Infinity', actual: finite ? 'all finite' : 'non-finite value found' },
    { name: 'non-negative speed', passed: nonNegativeSpeed, expected: 'speed ≥ 0', actual: `${a.speed.toFixed(4)} m/s` },
    { name: 'powered circuit motion', passed: poweredMotion, expected: 'train remains in motion after 20 s and exceeds lift speed', actual: `${a.speed.toFixed(4)} m/s now, ${a.peakSpeed.toFixed(4)} m/s peak` },
    { name: 'flat-track drag', passed: flatSpeed < 12 && flatSpeed > 9, expected: '12 m/s decelerates but does not stop in 1 s', actual: `${flatSpeed.toFixed(4)} m/s (${flatPoints.length} analytical markers)` },
    { name: 'loop closure', passed: track.frames[0].point.distanceTo(track.frames.at(-1)!.point) < 1e-6, expected: 'closed centerline seam < 1e-6 m', actual: `${track.frames[0].point.distanceTo(track.frames.at(-1)!.point).toExponential(2)} m` },
  ];
}
